import {
  ConfigForData,
  CustomComponentProps,
} from '@droplet-tech-code/core-common-types';
import { FormBuilder } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import {
  PurchaseGetCancelCalculationProviderResponse,
  PurchaseGetCancelCalculationResponse,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import { KeyVal, PaymentSummaryValue } from '@naus-code/naus-client-types';
import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { MutableRefObject, useEffect, useState } from 'react';

import { FormCurrencyNumber } from '~/components/Form/Form.CurrencyNumber';
import {
  ccyToSymbolDictionary,
  getPriceWithSymbol,
} from '~/screens/Support/Purchases/purchase.utils';
import { SquarePill } from '~/screens/Support/Requests/Pending/Flipper-SupportRequestsPending/Flipper.SupportRequestPending.ListItem';
import { translate } from '~/utils/translation.utils';
import { DATE_TIME_SERVER_FORMAT } from '~/utils/date.utils';

import { CancellationMethod, getCancelRefundType, getServiceFee } from './Cancel.utils';
import {
  ProviderGroup,
  cancelBookingManager,
  getGroupColor,
} from './CancelBooking.utils';

export const expectedRefundFieldId = (code: string) =>
  `expectedRefundFromCompany__${code}`;
export const actualRefundFieldId = (code: string) => `actualRefundFromCompany__${code}`;

// Actual-refund input with the over-100% warning rendered directly beneath it.
// Kept at module level (not an inline arrow) so FormBuilder doesn't remount the
// input — and drop focus — whenever the warning state changes.
const ActualRefundCurrencyNumber = (props: CustomComponentProps<any>) => {
  const { providerReservationCode, currency, maxRefundMinor } = props.config.meta ?? {};
  const [refundOverage] = cancelBookingManager.state.useGlobalState('refundOverage');
  const exceeded = !!refundOverage?.codes.includes(providerReservationCode);

  return (
    <VStack space="1">
      <FormCurrencyNumber {...props} />
      {exceeded ? (
        <Text.Small color="warning-mid">
          {translate('purchases.purchaseTool.refundExceedsCompanyValueWarning', {
            max: getPriceWithSymbol(
              maxRefundMinor,
              ccyToSymbolDictionary[currency as keyof typeof ccyToSymbolDictionary] ??
                `${currency} `,
            ),
          })}
        </Text.Small>
      ) : null}
    </VStack>
  );
};

interface CancelBookingFormProps {
  formHandlerRef: MutableRefObject<FormHandler>;
  method: CancellationMethod;
  providerGroups: ProviderGroup[];
  calculation?: PurchaseGetCancelCalculationResponse;
  providerCalculation?: PurchaseGetCancelCalculationProviderResponse;
  supportRequest?: SupportRequestBackendClient;
  purchaseCount?: number;
}

function getTotalRefundFromProviderCodes(
  calculation?: PurchaseGetCancelCalculationResponse,
) {
  if (!calculation) return 0;
  const refunds = (calculation as any)
    .refundsByProviderCode as KeyVal<PaymentSummaryValue>;
  const totalRefund = Object.values(refunds).reduce((cache, refundSummary) => {
    return cache + refundSummary.valueInBookCcy;
  }, 0);
  return totalRefund;
}

export const CancelBookingForm = (props: CancelBookingFormProps) => {
  const {
    formHandlerRef,
    method,
    providerGroups,
    calculation,
    providerCalculation,
    supportRequest,
    purchaseCount = 1,
  } = props;
  console.log({ providerCalculation });

  const initialRefundMethod = getCancelRefundType(supportRequest);
  const customerTotal = getTotalRefundFromProviderCodes(calculation) / purchaseCount;
  const serviceFeeDefault =
    getServiceFee(initialRefundMethod || 'bank', {
      formHandlerRef,
      purchaseCalculation: calculation,
      supportRequest,
    }) || 0;

  const [panelState, setPanelState] = useState<KeyVal<any>>({});
  // Provider cancellation type is mirrored into React state so the config can
  // include/exclude type-dependent fields. Form `conditions` can't do this:
  // they only evaluate on change events, so a field conditioned on the
  // initially-selected type would never render on mount.
  // Direct Ferries has no API cancel flow, so those bookings open on external.
  const [providerType, setProviderType] = useState<'api' | 'external'>(() => {
    const allDirectFerries =
      providerGroups.length > 0 &&
      providerGroups.every((g) =>
        g.purchases.every((p) => p.provider === 'DIRECT_FERRIES'),
      );
    return allDirectFerries ? 'external' : 'api';
  });

  useEffect(() => {
    const next: KeyVal<any> = {};
    const unsubscribe = formHandlerRef.current.subscribeToValueChange((id, value) => {
      next[id] = value;
      if (id === 'type' && (value === 'api' || value === 'external')) {
        setProviderType(value);
      }
    });
    setPanelState(next);
    return () => {
      unsubscribe();
    };
  }, [method]);

  const isProtection = calculation?.reason === 'CFAR' || calculation?.reason === 'KOALA';

  // External cancels, non-protection: company and customer refunds are usually
  // proportional. When the agent enters an actual refund for a provider group,
  // derive the refund fraction (entered / group company value) and apply it to
  // the group's customer-ccy value, auto-filling the customer refund field.
  // The fraction is capped at 100%; any overage is surfaced as a warning.
  // The field stays independently editable — we only write on actual-refund edits.
  //
  // Number inputs with `multipleOf` emit raw display text to subscribers,
  // return minor units from getSingleValue, and expect display text in
  // setInputValue — hence the ×100 / ÷100 conversions below.
  useEffect(() => {
    if (
      providerType !== 'external' ||
      isProtection ||
      method !== 'customer_and_provider'
    ) {
      return;
    }
    const form = formHandlerRef.current;
    const unsubscribe = form.subscribeToValueChange((id, value) => {
      const changedGroup = providerGroups.find(
        (g) => id === actualRefundFieldId(g.providerReservationCode),
      );
      if (!changedGroup) return;

      const changedActualMinor =
        (Number(String(value ?? '').replace(/,/g, '')) || 0) * 100;

      const exceededCodes: string[] = [];
      const customerRefundMinor = providerGroups.reduce((sum, g) => {
        const actualMinor =
          g === changedGroup
            ? changedActualMinor
            : Number(
                form.getSingleValue({
                  id: actualRefundFieldId(g.providerReservationCode),
                  validate: false,
                  showFeedback: false,
                }),
              ) || 0;
        const companyValue = g.purchases.reduce((s, p) => s + p.valueCompanyCcy, 0);
        const customerValue = g.purchases.reduce((s, p) => s + p.valueCcy, 0);
        if (companyValue <= 0) return sum;
        const fraction = actualMinor / companyValue;
        if (fraction > 1) exceededCodes.push(g.providerReservationCode);
        return sum + Math.min(fraction, 1) * customerValue;
      }, 0);

      cancelBookingManager.state.setGlobalState(
        'refundOverage',
        exceededCodes.length > 0 ? { codes: exceededCodes } : undefined,
      );

      form.setInputValue(
        'customerRefundInBookCcy',
        String(Math.round(customerRefundMinor) / 100),
      );
    });
    return () => {
      unsubscribe();
      cancelBookingManager.state.setGlobalState('refundOverage', undefined);
    };
  }, [method, providerType, isProtection, providerGroups]);

  const config: ConfigForData = [];

  // Provider type (only for provider / customer_and_provider modes)
  if (method === 'provider' || method === 'customer_and_provider') {
    config.push({
      items: [
        {
          id: 'type',
          type: 'single-select',
          list: [
            {
              value: 'external',
              label: translate('purchases.purchaseTool.externally'),
            },
            {
              value: 'api',
              label: translate('purchases.purchaseTool.apiCancel'),
            },
          ],
          initialValue: providerType,
          label: translate('purchases.purchaseTool.providerCancellationType'),
          feedbackOnlyWithError: true,
        },
      ],
    });
  }

  // Per-group operator-refund inputs
  if (method === 'provider' || method === 'customer_and_provider') {
    for (const group of providerGroups) {
      const providerRefundsByCode =
        (providerCalculation as any)?.refundsByProviderCode ??
        ((calculation as any)?.refundsByProviderCode as
          | KeyVal<PaymentSummaryValue>
          | undefined);
      const expectedDefault =
        (providerRefundsByCode?.[group.providerReservationCode]?.valueInOriginalCcy ??
          0) / purchaseCount;
      console.log('providerCode: ', group.providerReservationCode);
      const groupLabel = translate('purchases.purchaseTool.providerGroup', {
        code: group.providerReservationCode,
      });

      // Expected refund is only asked for API cancels; for external cancels
      // the operator enters the actual refund instead (expected is mirrored
      // from it at submit time — see CancelBooking.Footer).
      if (providerType === 'api') {
        config.push({
          label: groupLabel,
          items: [
            {
              id: expectedRefundFieldId(group.providerReservationCode),
              type: 'custom',
              identifier: 'currency-number',
              label: `${translate(
                'purchases.purchaseTool.expectedRefundFromCompany',
              )} — ${group.providerReservationCode}`,
              feedbackOnlyWithError: true,
              meta: {
                currency: group.purchases[0].companyCcy,
                multipleOf: 100,
                initialValue: expectedDefault,
              },
            },
          ],
        });
      } else {
        config.push({
          label: groupLabel,
          items: [
            {
              id: actualRefundFieldId(group.providerReservationCode),
              type: 'custom',
              identifier: 'currency-number',
              label: `${translate(
                'purchases.purchaseTool.actualRefundFromCompany',
              )} — ${group.providerReservationCode}`,
              feedbackOnlyWithError: true,
              meta: {
                currency: group.purchases[0].companyCcy,
                multipleOf: 100,
                providerReservationCode: group.providerReservationCode,
                maxRefundMinor: group.purchases.reduce(
                  (s, p) => s + p.valueCompanyCcy,
                  0,
                ),
              },
            },
          ],
        });
      }
    }

    // External cancel-date once for the whole booking
    if (providerType === 'external') {
      config.push({
        items: [
          {
            id: 'cancelDate',
            type: 'date',
            feedbackOnlyWithError: true,
            format: DATE_TIME_SERVER_FORMAT,
            label: translate('purchases.purchaseTool.cancelDate'),
            initialValue: dayjs().format(DATE_TIME_SERVER_FORMAT),
          },
        ],
      });
    }
  }

  // Customer-side fields
  if (method === 'customer' || method === 'customer_and_provider') {
    config.push({
      items: [
        {
          id: 'customerRefundInBookCcy',
          type: 'custom',
          identifier: 'currency-number',
          label: translate('purchases.purchaseTool.expectedRefund'),
          feedbackOnlyWithError: true,
          meta: {
            currency: providerGroups[0].purchases[0].ccy,
            multipleOf: 100,
            initialValue: customerTotal,
          },
        },
      ],
    });

    config.push({
      items: [
        {
          id: 'refundMethod',
          type: 'single-select',
          label: translate('purchases.purchaseTool.refundMethod'),
          list: [
            {
              value: 'bank',
              label: translate('purchases.purchaseTool.bankRefund'),
            },
            {
              value: 'voucher',
              label: translate('purchases.purchaseTool.voucherRefund'),
            },
          ],
          feedbackOnlyWithError: true,
          initialValue: initialRefundMethod || 'bank',
        },
      ],
    });

    config.push({
      items: [
        {
          id: 'serviceFeeInBookCcy',
          type: 'custom',
          identifier: 'currency-number',
          label: translate('purchases.purchaseTool.serviceFee'),
          feedbackOnlyWithError: true,
          meta: {
            currency: providerGroups[0].purchases[0].ccy,
            multipleOf: 100,
            initialValue: serviceFeeDefault,
          },
        },
      ],
    });
  }

  // Register a currency-prefixed number input for every `custom` field so the
  // currency shows to the left of the input rather than only in the label.
  const actualRefundIds = new Set(
    providerGroups.map((g) => actualRefundFieldId(g.providerReservationCode)),
  );
  const currencyExtensions = {
    custom: config
      .flatMap((section) => ('items' in section ? section.items : []))
      .filter((item) => item.type === 'custom')
      .map((item) => ({
        id: item.id,
        component: actualRefundIds.has(item.id)
          ? ActualRefundCurrencyNumber
          : FormCurrencyNumber,
      })),
  };

  return (
    <VStack space="3">
      {(method === 'provider' || method === 'customer_and_provider') &&
      providerGroups.length > 0 ? (
        <View px="3">
          <HStack space="2" align="center" style={{ flexWrap: 'wrap' }}>
            {providerGroups.map((g) => {
              const palette = getGroupColor(g.colorIndex);
              return (
                <SquarePill
                  key={g.providerReservationCode}
                  text={`${g.providerReservationCode} (${g.purchases.length})`}
                  bgColor={palette.bg}
                  fColor={palette.fg}
                />
              );
            })}
          </HStack>
        </View>
      ) : null}

      <FormBuilder
        key={`${method}__${panelState['refundMethod'] ?? ''}__${providerType}`}
        data={config}
        form={formHandlerRef.current}
        extensions={currencyExtensions}
      />
    </VStack>
  );
};
